/*  MUI  */
import {
    ExpandLess,
    ExpandMore,
    UnfoldLess,
    UnfoldMore
} from '@mui/icons-material';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

/*  Shared  */
import { Sum } from 'application/Shared/Components/Cards';
import {
    DateFormat,
    Mock,
    getDateTime
} from 'application/Shared/Components/Core';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Helpers } from 'application/Shared/Helpers';
import { Checkmark } from 'application/Shared/Icons';
import { debug } from 'application/Shared/Utils';

/*  Objects  */
import { User, UserFromContext } from 'objects/User';

/*  React  */
import React, { Component } from 'react';
import { CSVLink } from 'react-csv';
import { withTranslation } from 'react-i18next';

const csvExportFields = [
    { key: 'platformTenantId', label: 'Tenant ID' },
    { key: 'tenantName', label: 'Tenant Name' },
    { key: 'name', label: 'User' },
    { key: 'lastCheckIn', label: 'Last Check-In', transform: 'date' },
    { key: 'deviceAliases', label: 'Devices' },
    { key: 'upnAliases', label: 'UPNs' },
    { key: 'browserAliases', label: 'Browser Aliases' },
    { key: 'adSynced', label: 'Microsoft Entra ID Sync', transform: 'bool_01' }
];

const csvLinkId = 'csv_link_for_user_export_id';

const renderAliases = (p) => {
    const { id, aliases } = p;

    if (!aliases || !aliases.length) {
        return null;
    }

    return (
        <Grid container spacing={1}>
            {aliases.map((alias) => (
                <Grid key={`${id}_alias_${alias}`} item xs={12}>
                    <small>{alias}</small>
                </Grid>
            ))}
        </Grid>
    );
};

const renderUserRow = (p) => {
    const { user, client } = p;
    const {
        id,
        lastCheckIn,
        adSynced,
        deviceAliases,
        upnAliases,
        browserAliases
    } = user;
    const clientId = client.id;

    return (
        <TableRow key={`${clientId}_user_${id}`}>
            <TableCell>
                <User data={user}>
                    <UserFromContext includeTitle />
                </User>
            </TableCell>
            <TableCell>
                <DateFormat date={lastCheckIn} noTz small />
            </TableCell>
            <TableCell>
                {renderAliases({ aliases: deviceAliases, id })}
            </TableCell>
            <TableCell>{renderAliases({ aliases: upnAliases, id })}</TableCell>
            <TableCell>
                {renderAliases({ aliases: browserAliases, id })}
            </TableCell>
            <TableCell align="center">
                <Checkmark checked={adSynced} />
            </TableCell>
        </TableRow>
    );
};

const prepareOneClientForCSV = (client) => {
    const { name: tenantName, users } = client;

    const exportData = JSON.parse(JSON.stringify(users));
    return exportData.map((r) => {
        const row = r;
        row.tenantName = tenantName;

        csvExportFields.forEach((field) => {
            const { key, transform } = field;
            if (!Helpers.isset(row[key])) {
                if (transform === 'bool_01') {
                    row[key] = 0;
                }
                return;
            }

            let val = row[key];

            if (typeof val === 'object') {
                val = val.join(',');
                row[key] = `${val}`;
                return;
            }

            if (transform === 'date') {
                row[key] = getDateTime({ date: row[key] });
            }

            if (transform === 'bool_01') {
                row[key] = val ? '1' : '0';
            }
        });

        return row;
    });
};

const createFileName = (tenantName) => {
    const now = new Date();
    let csvFilename = tenantName !== '' ? `${tenantName}-` : '';
    csvFilename += `user_export-`;
    csvFilename += `${(now.getMonth() + 1).toString().padStart(2, '0')}-`;
    csvFilename += `${now.getDate().toString().padStart(2, '0')}-`;
    csvFilename += `${now.getFullYear().toString().padStart(2, '0')}_`;
    csvFilename += `${now.getHours().toString().padStart(2, '0')}-`;
    csvFilename += `${now.getMinutes().toString().padStart(2, '0')}_`;
    csvFilename += now.getSeconds().toString().padStart(2, '0');
    return csvFilename;
};
class PartnerUsersReport extends Component {
    constructor(props) {
        super(props);

        this.state = {
            csvFilename: '',
            csvReady: false,
            expandedMap: {},
            exportData: {},
            loaded: false,
            showCollapse: false,
            showExpand: true
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { reportData } = this.props;
        if (!Helpers.isset(reportData, 'clients', 0, 'id')) {
            debug.e('No Data Found');
            return;
        }

        const { clients } = reportData;
        const expandedMap = {};
        clients.forEach((r) => {
            expandedMap[r.id] = false;
        });

        this.setState({ expandedMap, loaded: true });
    };

    handleAll = (expanded) => {
        const { expandedMap } = this.state;

        Object.keys(expandedMap).forEach((k) => {
            expandedMap[k] = expanded;
        });

        this.setState({
            expandedMap,
            showCollapse: expanded,
            showExpand: !expanded
        });
    };

    handleExpand = (p) => {
        const { expandedMap } = this.state;
        const { id } = p;

        expandedMap[id] = !expandedMap[id];

        let showCollapse = false;
        let showExpand = false;
        Object.values(expandedMap).forEach((expanded) => {
            if (expanded) {
                showCollapse = true;
            } else {
                showExpand = true;
            }
        });

        this.setState({ expandedMap, showCollapse, showExpand });
    };

    renderCharts = () => {
        const { t, reportData } = this.props;
        const { counts } = reportData;

        return (
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <Sum
                        sum={counts.clients}
                        title={t('Common.total_clients')}
                        height={300}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Sum
                        sum={counts.users}
                        title={t('Common.total_users')}
                        height={300}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Sum
                        sum={counts.devices}
                        title={t('Common.total_devices')}
                        height={300}
                    />
                </Grid>
            </Grid>
        );
    };

    renderHeaders = () => {
        const { t, reportSetup } = this.props;
        const { includeInactive } = reportSetup;

        return [
            { field: 'expand', renderHeader: '', width: '5%' },
            {
                field: 'client',
                renderHeader: t('Common.client'),
                width: '25%'
            },
            {
                align: 'left',
                field: 'users',
                renderHeader: includeInactive
                    ? t('Common.users')
                    : t('Common.active_users'),
                width: '20%'
            },
            {
                align: 'left',
                field: 'applications',
                renderHeader: t('Common.applications'),
                width: '20%'
            },
            {
                align: 'left',
                field: 'devices',
                renderHeader: t('Common.devices'),
                width: '20%'
            },
            { field: 'actions', renderHeader: '', width: '10%' }
        ];
    };

    renderButtons = () => {
        const { t } = this.props;
        const { showExpand, showCollapse } = this.state;

        return (
            <Box mb="16px" style={{ float: 'right' }}>
                {showCollapse && (
                    <Button
                        data-trackid="reports_partnerUsers_collapseAll"
                        variant="outlined"
                        className="no-print"
                        color="primary"
                        onClick={() => this.handleAll(false)}
                        style={{ marginRight: showExpand ? '8px' : 0 }}
                    >
                        <UnfoldLess fontSize="small" />
                        {t('Reports.collapse_all')}
                    </Button>
                )}
                {showExpand && (
                    <Button
                        data-trackid="reports_partnerUsers_expandAll"
                        variant="contained"
                        className="no-print"
                        color="primary"
                        onClick={() => this.handleAll(true)}
                    >
                        <UnfoldMore fontSize="small" />
                        {t('Reports.expand_all')}
                    </Button>
                )}
                <Button
                    data-trackid="reports_partnerUsers_exportAll"
                    variant="outlined"
                    color="primary"
                    style={{ marginLeft: '8px' }}
                    onClick={this.handleExportAll}
                >
                    {t('Common.export_all')}
                </Button>
            </Box>
        );
    };

    handleExportAll = () => {
        const { reportData } = this.props;
        const { clients } = reportData;

        const allExportData = clients.map(prepareOneClientForCSV).flat();

        const csvFilename = createFileName('');
        this.setState(
            {
                csvFilename,
                csvReady: true,
                exportData: allExportData
            },
            () => {
                document.getElementById(csvLinkId).click();
            }
        );
    };

    handleExportRow = (client) => {
        const { name } = client;

        const exportData = prepareOneClientForCSV(client);
        const csvFilename = createFileName(name);

        this.setState(
            {
                csvFilename,
                csvReady: true,
                exportData
            },
            () => {
                document.getElementById(csvLinkId).click();
            }
        );
    };

    renderClientRow = (p) => {
        const { t } = this.props;
        const { expandedMap } = this.state;
        const { client } = p;
        const { id, name, userCount, appCount, deviceCount } = client;
        const expanded = expandedMap[id];

        return (
            <TableRow
                sx={{
                    backgroundColor: 'table.rowBackground'
                }}
            >
                <TableCell>
                    <IconButton
                        data-trackid={
                            expanded
                                ? 'reports_partnerUsers_collapseRow'
                                : 'reports_partnerUsers_expandRow'
                        }
                        aria-label="expand row"
                        size="small"
                        onClick={() => this.handleExpand(client)}
                        sx={{ color: 'icon.textColor' }}
                    >
                        {expanded ? (
                            <ExpandLess fontSize="small" />
                        ) : (
                            <ExpandMore fontSize="small" />
                        )}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Mock id={id} report>
                        <Typography
                            variant="span"
                            sx={{
                                '&:hover': {
                                    borderBottom: '1px solid',
                                    borderColor: 'border.tertiary',
                                    color: 'hover.tertiary'
                                },
                                cursor: 'pointer',
                                fontWeight: 600
                            }}
                        >
                            {name}
                        </Typography>
                    </Mock>
                </TableCell>
                <TableCell align="left">{userCount}</TableCell>
                <TableCell align="left">{appCount}</TableCell>
                <TableCell align="left">{deviceCount}</TableCell>
                <TableCell align="right">
                    <Button
                        data-trackid="reports_partnerUsers_export"
                        variant="outlined"
                        onClick={() => this.handleExportRow(client)}
                    >
                        {t('Common.export')}
                    </Button>
                </TableCell>
            </TableRow>
        );
    };

    renderUsers = (p) => {
        const { t } = this.props;
        const { client } = p;
        const { users } = client;

        return (
            <TableContainer>
                <Table size="small">
                    <TableHead
                        sx={{
                            backgroundColor: 'table.headerRowBackground'
                        }}
                    >
                        <TableRow variant="noHover">
                            <TableCell style={{ width: '25%' }}>
                                <strong>{t('Common.user')}</strong>
                            </TableCell>
                            <TableCell style={{ width: '10%' }}>
                                <strong>{t('Common.last_check_in')}</strong>
                            </TableCell>
                            <TableCell style={{ width: '20%' }}>
                                <strong>{t('Common.devices')}</strong>
                            </TableCell>
                            <TableCell style={{ width: '15%' }}>
                                <strong>{t('Common.upns')}</strong>
                            </TableCell>
                            <TableCell style={{ width: '20%' }}>
                                <strong>{t('Common.browser_aliases')}</strong>
                            </TableCell>
                            <TableCell align="center" style={{ width: '10%' }}>
                                <strong>
                                    {t('Common.microsoftEntraID.sync')}
                                </strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={{
                            backgroundColor: 'table.subtableBackground'
                        }}
                    >
                        {users.map((user) => renderUserRow({ client, user }))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    renderRow = (row) => {
        const { expandedMap } = this.state;
        const client = row;
        const { id } = client;
        const expanded = expandedMap[id];

        return (
            <React.Fragment key={`client_${id}`}>
                {this.renderClientRow({ client })}
                {expanded && (
                    <TableRow variant="noHover">
                        <TableCell colSpan="6">
                            {this.renderUsers({ client })}
                        </TableCell>
                    </TableRow>
                )}
            </React.Fragment>
        );
    };

    renderTable = () => {
        const { csvReady, exportData, csvFilename } = this.state;
        const { reportData } = this.props;
        const { clients } = reportData;

        const tableHeaders = this.renderHeaders();

        return (
            <Box mt="16px">
                {this.renderButtons()}
                <ManagedTable
                    headers={tableHeaders}
                    manageRows
                    renderBody={this.renderRow}
                    renderBodyKey="id"
                    rows={clients}
                    rowsPerPage={-1}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="userCount"
                />
                {csvReady && (
                    <CSVLink
                        id={csvLinkId}
                        data={exportData}
                        headers={csvExportFields}
                        filename={csvFilename}
                    />
                )}
            </Box>
        );
    };

    render() {
        const { loaded } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Box>
                {this.renderCharts()}
                {this.renderTable()}
            </Box>
        );
    }
}

export default withTranslation()(PartnerUsersReport);
